import React, { useState, useEffect } from 'react';
import { API_URL } from "../config";
import { toast } from 'react-toastify';
import { 
  FaBuilding, FaEnvelope, 
  FaBell, FaCreditCard, FaSave, 
  FaCheck, FaGasPump, FaSpinner, FaBullseye
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import CompanyProfileTab from '../components/CompanyProfileTab';
import { rateCards } from '../api';

function SettingsPage({ onLogout }) {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});

  // ===== Rate Card (container engine) admin knobs =====
  const [rateVars, setRateVars] = useState({});
  const [rateVarsLoaded, setRateVarsLoaded] = useState(false);
  const [rateVarsSaving, setRateVarsSaving] = useState(false);

  // Bulk save for the 12-month Targets grid
  const [savingAllTargets, setSavingAllTargets] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // The next twelve months, starting with the current one — the Targets grid feeds off this.
  const targetMonths = (() => {
    const out = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + i, 1));
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      out.push({ key, label: d.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' }) });
    }
    return out;
  })();

  useEffect(() => {
    fetchSettings();
  }, []);

  // Lazy-load rate card variables when the Rate Card tab is opened
  useEffect(() => {
    if (activeTab === 'ratecard' && !rateVarsLoaded) {
      loadRateVars();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  const loadRateVars = async () => {
    try {
      const res = await rateCards.getVariables();
      if (res.success) {
        setRateVars(res.variables || {});
        setRateVarsLoaded(true);
      } else {
        toast.error(res.error || 'Failed to load rate card settings');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load rate card settings');
    }
  };

  const handleSaveRateVars = async () => {
    setRateVarsSaving(true);
    try {
      const payload = {
        fuel_price_per_l: parseFloat(rateVars.fuel_price_per_l) || 0,
        fuel_index_base: parseFloat(rateVars.fuel_index_base) || 0,
        profit_margin: (parseFloat(rateVars.profit_margin_pct) || 0) / 100,
        cpi_rate: (parseFloat(rateVars.cpi_rate_pct) || 0) / 100,
        border_surcharge: parseFloat(rateVars.border_surcharge) || 0
      };
      const res = await rateCards.updateVariables(payload);
      if (res.success) {
        toast.success('Rate card settings saved — new container quotes will use them');
      } else {
        toast.error(res.error || 'Failed to save rate card settings');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save rate card settings');
    } finally {
      setRateVarsSaving(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/settings`);
      const data = await response.json();
      if (data.success) {
        setSettings(data.settings || {});
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSaveSetting = async (category, key, value) => {
    try {
      const response = await fetch(`${API_URL}/settings/${category}/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value })
      });
      const data = await response.json();
      if (data.success) {
        setSettings({
          ...settings,
          [category]: {
            ...settings[category],
            [key]: value
          }
        });
        toast.success('Setting saved successfully!');
      } else {
        // Was silent before: a rejected save looked exactly like a successful one.
        toast.error(data.error || 'Failed to save setting');
      }
    } catch (error) {
      console.error('Error saving setting:', error);
      toast.error('Failed to save setting');
    }
  };

  // Saves every month in the Targets grid in one go — twelve rows shouldn't mean twelve clicks.
  const handleSaveAllTargets = async () => {
    setSavingAllTargets(true);
    try {
      let saved = 0;
      const failures = [];
      for (const m of targetMonths) {
        const key = `revenue_${m.key}`;
        const value = settings.targets?.[key];
        if (value === null || value === undefined || String(value).trim() === '') continue;
        const response = await fetch(`${API_URL}/settings/targets/${key}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value })
        });
        const data = await response.json();
        if (data.success) saved += 1;
        else failures.push(m.label);
      }
      if (failures.length) {
        toast.error(`Saved ${saved} month(s) — failed: ${failures.join(', ')}`);
      } else if (saved === 0) {
        toast.info('No month targets filled in yet — enter a figure first');
      } else {
        toast.success(`Saved ${saved} month target${saved === 1 ? '' : 's'}`);
      }
      await fetchSettings();
    } catch (error) {
      console.error('Error saving month targets:', error);
      toast.error('Failed to save month targets');
    } finally {
      setSavingAllTargets(false);
    }
  };

  return (
    <AdminLayout activeTab="settings" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">⚙️ Settings</h1>
          <p className="text-gray-500 text-sm">Manage system settings</p>
        </div>
      </div>

      {/* Tabs - Users tab removed */}
      <div className="flex gap-2 mb-6 bg-white rounded-xl p-2 border border-gray-200 overflow-x-auto">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'general' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaBuilding className="inline mr-2" /> General
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'email' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaEnvelope className="inline mr-2" /> Email
        </button>
        <button
          onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'notifications' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaBell className="inline mr-2" /> Notifications
        </button>
        <button
          onClick={() => setActiveTab('billing')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'billing' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaCreditCard className="inline mr-2" /> Billing
        </button>
        <button
          onClick={() => setActiveTab('targets')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'targets' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaBullseye className="inline mr-2" /> Targets
        </button>
        <button
          onClick={() => setActiveTab('ratecard')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'ratecard' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaGasPump className="inline mr-2" /> Rate Card
        </button>
        <button
          onClick={() => setActiveTab('company')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'company' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaBuilding className="inline mr-2" /> Company Profile
        </button>
      </div>

      {/* Company Profile (secured single source) */}
      {activeTab === 'company' && (
        <CompanyProfileTab user={user} />
      )}

      {/* Targets — Jeeves reads these when he briefs you */}
      {activeTab === 'targets' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-1">Monthly Targets</h3>
          <p className="text-sm text-gray-500 mb-5">
            Jeeves uses these when he briefs you on the dashboard — he will tell you what the month's pace
            implies against them. Leave a target blank and he simply reports the projection without comparing
            it to anything, rather than guessing at a figure.
          </p>

          <div className="space-y-5 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default monthly revenue target (excl VAT)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={settings.targets?.revenue_monthly || ''}
                  onChange={(e) => setSettings({ ...settings, targets: { ...settings.targets, revenue_monthly: e.target.value } })}
                  placeholder="e.g. 500000"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
                <button
                  onClick={() => handleSaveSetting('targets', 'revenue_monthly', settings.targets?.revenue_monthly)}
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm flex items-center gap-2 hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} /> Save
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gross margin target (%)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={settings.targets?.gross_margin_pct || ''}
                  onChange={(e) => setSettings({ ...settings, targets: { ...settings.targets, gross_margin_pct: e.target.value } })}
                  placeholder="e.g. 20"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
                <button
                  onClick={() => handleSaveSetting('targets', 'gross_margin_pct', settings.targets?.gross_margin_pct)}
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm flex items-center gap-2 hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} /> Save
                </button>
              </div>
            </div>
          </div>

          <div className="border-t pt-5" style={{ borderColor: '#E8ECEF' }}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
              <h4 className="text-sm font-semibold text-navy">Month by month</h4>
              <button
                onClick={handleSaveAllTargets}
                disabled={savingAllTargets}
                className="px-3 py-1.5 bg-navy text-white rounded-lg text-xs flex items-center gap-2 hover:bg-green hover:text-navy transition-colors disabled:opacity-50"
              >
                {savingAllTargets ? <FaSpinner className="animate-spin" size={12} /> : <FaSave size={12} />}
                {savingAllTargets ? 'Saving…' : 'Save all months'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              The current month and the eleven that follow. A month with its own target overrides the default
              above; a blank month simply falls back to it. The dashboard's revenue chart compares what was
              actually invoiced against these.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {targetMonths.map((m) => {
                const key = `revenue_${m.key}`;
                return (
                  <div key={m.key} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-20 flex-shrink-0">{m.label}</span>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={settings.targets?.[key] || ''}
                      onChange={(e) => setSettings({ ...settings, targets: { ...settings.targets, [key]: e.target.value } })}
                      placeholder={settings.targets?.revenue_monthly ? `default ${settings.targets.revenue_monthly}` : 'no target'}
                      className="flex-1 min-w-0 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
                    />
                    <button
                      onClick={() => handleSaveSetting('targets', key, settings.targets?.[key])}
                      className="px-2.5 py-1.5 bg-navy text-white rounded-lg text-xs flex items-center gap-1 hover:bg-green hover:text-navy transition-colors flex-shrink-0"
                    >
                      <FaSave size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4">General Settings</h3>
          <div className="mb-4 p-3 rounded-xl bg-blue-50 text-blue-700 text-sm">
            Company name, reg, VAT, address and bank details are managed on the <strong>Company Profile</strong> tab (passcode + second-admin approval required).
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              <div className="flex gap-2">
                <select
                  value={settings.general?.timezone || 'Africa/Johannesburg'}
                  onChange={(e) => setSettings({
                    ...settings,
                    general: { ...settings.general, timezone: e.target.value }
                  })}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                >
                  <option value="Africa/Johannesburg">Africa/Johannesburg</option>
                  <option value="Africa/Cairo">Africa/Cairo</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/New_York">America/New_York</option>
                </select>
                <button
                  onClick={() => handleSaveSetting('general', 'timezone', settings.general?.timezone)}
                  className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <div className="flex gap-2">
                <select
                  value={settings.general?.currency || 'ZAR'}
                  onChange={(e) => setSettings({
                    ...settings,
                    general: { ...settings.general, currency: e.target.value }
                  })}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                >
                  <option value="ZAR">ZAR - South African Rand</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
                <button
                  onClick={() => handleSaveSetting('general', 'currency', settings.general?.currency)}
                  className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Settings */}
      {activeTab === 'email' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4">Email Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={settings.email?.smtp_host || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    email: { ...settings.email, smtp_host: e.target.value }
                  })}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  placeholder="smtp.gmail.com"
                />
                <button
                  onClick={() => handleSaveSetting('email', 'smtp_host', settings.email?.smtp_host)}
                  className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Port</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={settings.email?.smtp_port || 587}
                  onChange={(e) => setSettings({
                    ...settings,
                    email: { ...settings.email, smtp_port: e.target.value }
                  })}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
                <button
                  onClick={() => handleSaveSetting('email', 'smtp_port', settings.email?.smtp_port)}
                  className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={settings.email?.from_email || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    email: { ...settings.email, from_email: e.target.value }
                  })}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  placeholder="info@onepurposelogistics.com"
                />
                <button
                  onClick={() => handleSaveSetting('email', 'from_email', settings.email?.from_email)}
                  className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={settings.email?.from_name || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    email: { ...settings.email, from_name: e.target.value }
                  })}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  placeholder="One Purpose Logistics"
                />
                <button
                  onClick={() => handleSaveSetting('email', 'from_name', settings.email?.from_name)}
                  className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Settings */}
      {activeTab === 'notifications' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4">Notification Preferences</h3>
          <div className="space-y-4">
            {[
              { key: 'new_job_alert', label: 'New Job Created' },
              { key: 'subbie_accepted_alert', label: 'Subcontractor Accepted Job' },
              { key: 'trucks_allocated_alert', label: 'Trucks Allocated to Job' }
            ].map((item) => (
              <div key={item.key} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700">{item.label}</span>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => {
                      const currentValue = settings.notifications?.[item.key] === 'true';
                      const newValue = !currentValue;
                      setSettings({
                        ...settings,
                        notifications: { ...settings.notifications, [item.key]: String(newValue) }
                      });
                      handleSaveSetting('notifications', item.key, String(newValue));
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${settings.notifications?.[item.key] === 'true' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {settings.notifications?.[item.key] === 'true' ? <><FaCheck className="inline mr-1" /> Enabled</> : 'Disabled'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing Settings */}
      {activeTab === 'billing' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4">Billing Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Payment Terms</label>
              <div className="flex gap-2">
                <select
                  value={settings.billing?.default_payment_terms || '30 days'}
                  onChange={(e) => setSettings({
                    ...settings,
                    billing: { ...settings.billing, default_payment_terms: e.target.value }
                  })}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                >
                  <option value="7 days">7 days</option>
                  <option value="14 days">14 days</option>
                  <option value="30 days">30 days</option>
                  <option value="45 days">45 days</option>
                  <option value="60 days">60 days</option>
                </select>
                <button
                  onClick={() => handleSaveSetting('billing', 'default_payment_terms', settings.billing?.default_payment_terms)}
                  className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default VAT Rate (%)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={settings.billing?.vat_rate || 15}
                  onChange={(e) => setSettings({
                    ...settings,
                    billing: { ...settings.billing, vat_rate: e.target.value }
                  })}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  step="0.01"
                />
                <button
                  onClick={() => handleSaveSetting('billing', 'vat_rate', settings.billing?.vat_rate)}
                  className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
                >
                  <FaSave size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rate Card (container engine) settings */}
      {activeTab === 'ratecard' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-1">Container Rate Card — Admin Knobs</h3>
          <p className="text-sm text-gray-500 mb-4">
            Change the diesel price here and every <strong>new</strong> container quote recalculates automatically.
          </p>

          <div className="mb-5 p-3 rounded-xl bg-blue-50 text-blue-700 text-sm">
            <strong>Current fuel index:</strong>{' '}
            {(parseFloat(rateVars.fuel_price_per_l) || 0) > 0 && (parseFloat(rateVars.fuel_index_base) || 0) > 0 ? (
              <>R{(parseFloat(rateVars.fuel_price_per_l)).toFixed(2)}/L ÷ R{(parseFloat(rateVars.fuel_index_base)).toFixed(2)} = index <strong>{(parseFloat(rateVars.fuel_price_per_l) / parseFloat(rateVars.fuel_index_base)).toFixed(3)}</strong></>
            ) : (
              'Open the Rate Card tab to load the current settings'
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Diesel Price (R / litre)</label>
              <input type="number" step="0.01" value={rateVars.fuel_price_per_l ?? ''}
                onChange={(e) => setRateVars({ ...rateVars, fuel_price_per_l: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Index Base (R / litre)</label>
              <input type="number" step="0.01" value={rateVars.fuel_index_base ?? ''}
                onChange={(e) => setRateVars({ ...rateVars, fuel_index_base: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Profit Margin (%)</label>
              <input type="number" step="0.1" value={rateVars.profit_margin_pct ?? (rateVars.profit_margin != null ? rateVars.profit_margin * 100 : 5)}
                onChange={(e) => setRateVars({ ...rateVars, profit_margin_pct: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CPI Rate (% / year)</label>
              <input type="number" step="0.1" value={rateVars.cpi_rate_pct ?? (rateVars.cpi_rate != null ? rateVars.cpi_rate * 100 : 3)}
                onChange={(e) => setRateVars({ ...rateVars, cpi_rate_pct: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Border Surcharge (R)</label>
              <input type="number" step="10" value={rateVars.border_surcharge ?? ''}
                onChange={(e) => setRateVars({ ...rateVars, border_surcharge: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={handleSaveRateVars}
              disabled={rateVarsSaving}
              className="px-5 py-2 bg-navy text-white rounded-lg font-medium hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {rateVarsSaving ? <FaSpinner className="animate-spin" /> : <FaSave size={14} />} Save Rate Card Settings
            </button>
            <p className="text-xs text-gray-400">Applies to new quotes only — existing quotes keep their locked rate.</p>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default SettingsPage;